import { useEffect, useMemo, useState } from 'react'
import { ChevronDown, ChevronRight, Search } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { CultureBadge } from './CultureBadge'
import { cn } from '@/lib/utils'
import { showErrorToast } from '@/lib/toast'

import { getCatalogAttributes } from '@/lib/api/v2/catalog-translations'
import type { AttributeLabelEntry } from '@/types/catalog-translations'

/**
 * Read-only viewer for the shared attribute label keys used across the
 * catalog. Surfaces missing-translation gaps and per-key usages so an
 * admin can see at a glance which labels are reused everywhere.
 */
export function AttributeLabelsTable() {
  const [entries, setEntries] = useState<AttributeLabelEntry[]>([])
  const [cultures, setCultures] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [onlyMissing, setOnlyMissing] = useState(false)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const payload = await getCatalogAttributes()
        if (cancelled) return
        setEntries(payload.attributes)
        setCultures(payload.supportedCultures)
      } catch {
        if (!cancelled) showErrorToast('Erreur lors du chargement des labels d\'attributs.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => { cancelled = true }
  }, [])

  const filtered = useMemo(() => {
    let result = entries
    if (onlyMissing) result = result.filter(e => e.missingCultures.length > 0)
    const q = search.trim().toLowerCase()
    if (q) {
      result = result.filter(e =>
        e.labelKey.toLowerCase().includes(q) ||
        Object.values(e.labels).some(l => l.toLowerCase().includes(q))
      )
    }
    return result
  }, [entries, search, onlyMissing])

  function toggle(labelKey: string) {
    setExpanded(prev => ({ ...prev, [labelKey]: !prev[labelKey] }))
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Search className="size-4 text-muted-foreground" />
          <Input
            data-testid="catalog-attributes-search-input"
            type="search"
            placeholder="Rechercher une clé i18n ou un libellé…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="max-w-sm h-8"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
          <input
            data-testid="catalog-attributes-only-missing-toggle"
            type="checkbox"
            checked={onlyMissing}
            onChange={e => setOnlyMissing(e.target.checked)}
            className="size-3.5"
          />
          <span>Traductions manquantes uniquement</span>
        </label>
        <span className="text-xs text-muted-foreground ml-auto">
          {filtered.length} clé{filtered.length > 1 ? 's' : ''}
          {filtered.length !== entries.length && ` (sur ${entries.length})`}
        </span>
      </div>

      <div className="rounded-md border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr className="text-left">
              <th className="px-3 py-2 font-medium w-[8px]"></th>
              <th className="px-3 py-2 font-medium">Clé i18n</th>
              {cultures.map(c => (
                <th key={c} className="px-3 py-2 font-medium">
                  <CultureBadge culture={c} />
                </th>
              ))}
              <th className="px-3 py-2 font-medium text-right">Usages</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {loading && (
              <tr><td colSpan={2 + cultures.length + 1} className="px-3 py-6 text-center text-sm text-muted-foreground">Chargement…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={2 + cultures.length + 1} className="px-3 py-8 text-center text-sm text-muted-foreground">Aucune clé ne correspond.</td></tr>
            )}
            {!loading && filtered.map(entry => (
              <AttributeRow
                key={entry.labelKey}
                entry={entry}
                cultures={cultures}
                expanded={!!expanded[entry.labelKey]}
                onToggle={() => toggle(entry.labelKey)}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

interface AttributeRowProps {
  entry: AttributeLabelEntry
  cultures: string[]
  expanded: boolean
  onToggle: () => void
}

function AttributeRow({ entry, cultures, expanded, onToggle }: AttributeRowProps) {
  return (
    <>
      <tr
        data-testid={`catalog-attributes-row-${entry.labelKey}`}
        className="hover:bg-muted/30 cursor-pointer"
        onClick={onToggle}
      >
        <td className="px-3 py-2 align-middle">
          {expanded ? <ChevronDown className="size-3.5 text-muted-foreground" /> : <ChevronRight className="size-3.5 text-muted-foreground" />}
        </td>
        <td className="px-3 py-2 font-mono text-[12px] text-foreground/90">{entry.labelKey}</td>
        {cultures.map(c => {
          const isMissing = entry.missingCultures.includes(c)
          return (
            <td key={c} className="px-3 py-2">
              <span className={cn(
                'text-sm',
                isMissing ? 'text-destructive italic' : ''
              )}>
                {isMissing ? '(non traduit)' : (entry.labels[c] || '—')}
              </span>
            </td>
          )
        })}
        <td className="px-3 py-2 text-right">
          <Badge variant="secondary" className="text-[11px]">{entry.usages.length}</Badge>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-muted/20">
          <td colSpan={2 + cultures.length + 1} className="px-6 py-3">
            <div className="text-xs text-muted-foreground mb-2">
              Utilisé par {entry.usages.length} attribut{entry.usages.length > 1 ? 's' : ''} :
            </div>
            <div className="flex flex-wrap gap-1.5">
              {entry.usages.map(u => (
                <Badge key={`${u.productCode}.${u.attributeId}`} variant="outline" className="font-mono text-[10px]">
                  {u.productCode}.{u.attributeId}
                  <span className="ml-1 text-muted-foreground">[{u.type}]</span>
                </Badge>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  )
}
